import { useState, useEffect, useCallback } from 'react';
import type { ChatMessage, ChatSession } from '../types';
import {
  sendChatMessage,
  getChatHistory,
  createChatSession,
  listChatSessions,
  updateChatSession,
  deleteChatSession,
} from '../services/chatApi';

interface UseChatbotReturn {
  // State
  messages: ChatMessage[];
  sessions: ChatSession[];
  currentSession: ChatSession | null;
  isLoading: boolean;
  isTyping: boolean;
  error: string | null;

  // Actions
  sendMessage: (message: string) => Promise<void>;
  loadChatHistory: (sessionId: string) => Promise<void>;
  createNewSession: (title?: string) => Promise<void>;
  selectSession: (sessionId: string) => Promise<void>;
  deleteSession: (sessionId: string) => Promise<void>;
  updateSessionTitle: (sessionId: string, title: string) => Promise<void>;
  clearError: () => void;
  loadSessions: () => Promise<void>;
}

export const useChatbot = (): UseChatbotReturn => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load all chat sessions for the user
   */
  const loadSessions = useCallback(async () => {
    try {
      setError(null);
      const sessionList = await listChatSessions();
      setSessions(sessionList);
    } catch (err) {
      console.error('Failed to load sessions:', err);
      setError('Failed to load chat sessions');
    }
  }, []);

  /**
   * Load chat history for a specific session
   */
  const loadChatHistory = useCallback(async (sessionId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const history = await getChatHistory(sessionId);
      setMessages(history);
    } catch (err) {
      console.error('Failed to load chat history:', err);
      setError('Failed to load chat history');
      setMessages([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Create a new chat session (optimized with optimistic update)
   */
  const createNewSession = useCallback(async (title?: string) => {
    try {
      setError(null);
      
      // Optimistically clear messages immediately for snappy UX
      setMessages([]);
      
      // Create session in background
      const newSession = await createChatSession({ title });
      setCurrentSession(newSession);
      
      // Load sessions in background without blocking
      loadSessions().catch(console.error);
    } catch (err) {
      console.error('Failed to create session:', err);
      setError('Failed to create new chat session');
    }
  }, [loadSessions]);

  /**
   * Select an existing session
   */
  const selectSession = useCallback(async (sessionId: string) => {
    try {
      setError(null);
      const session = sessions.find((s) => s.id === sessionId);
      if (session) {
        setCurrentSession(session);
        await loadChatHistory(sessionId);
      } else {
        setError('Session not found');
      }
    } catch (err) {
      console.error('Failed to select session:', err);
      setError('Failed to select session');
    }
  }, [sessions, loadChatHistory]);

  /**
   * Delete a chat session
   */
  const deleteSession = useCallback(async (sessionId: string) => {
    try {
      setError(null);
      await deleteChatSession(sessionId);
      
      // If deleting current session, clear it
      if (currentSession?.id === sessionId) {
        setCurrentSession(null);
        setMessages([]);
      }
      
      // Reload sessions list
      await loadSessions();
    } catch (err) {
      console.error('Failed to delete session:', err);
      setError('Failed to delete session');
    }
  }, [currentSession, loadSessions]);

  /**
   * Update session title
   */
  const updateSessionTitle = useCallback(async (sessionId: string, title: string) => {
    try {
      setError(null);
      const updatedSession = await updateChatSession(sessionId, { title });
      
      // Update current session if it's the one being updated
      if (currentSession?.id === sessionId) {
        setCurrentSession(updatedSession);
      }
      
      // Reload sessions list
      await loadSessions();
    } catch (err) {
      console.error('Failed to update session title:', err);
      setError('Failed to update session title');
    }
  }, [currentSession, loadSessions]);

  /**
   * Send a message to the chatbot (optimized for snappy UX)
   */
  const sendMessage = useCallback(async (message: string) => {
    if (!message.trim()) return;

    try {
      setError(null);
      
      // Start typing animation IMMEDIATELY for responsive feel
      setIsTyping(true);

      // Create a session if one doesn't exist
      let sessionId = currentSession?.id;
      if (!sessionId) {
        const newSession = await createChatSession({ title: message.slice(0, 50) });
        setCurrentSession(newSession);
        sessionId = newSession.id;
        // Load sessions in background without blocking
        loadSessions().catch(console.error);
      }

      // Add user message to UI immediately (optimistic update)
      const userMessage: ChatMessage = {
        id: `temp-${Date.now()}`,
        user_id: '',
        session_id: sessionId,
        message: message.trim(),
        role: 'user',
        created_at: new Date().toISOString(),
        metadata: {},
      };
      setMessages((prev) => [...prev, userMessage]);

      // Send message to backend
      const response = await sendChatMessage({
        message: message.trim(),
        sessionId,
      });

      // Replace temporary user message with the saved one and add AI response
      setMessages((prev) => {
        const filtered = prev.filter((m) => m.id !== userMessage.id);
        return [...filtered, response.userMessage, response.assistantMessage];
      });

      // Check if the AI response indicates a task was created/updated
      const aiResponse = response.assistantMessage.message.toLowerCase();
      if (
        aiResponse.includes('task created') ||
        aiResponse.includes('✅') ||
        aiResponse.includes('created the') ||
        aiResponse.includes('updated') ||
        aiResponse.includes('deleted')
      ) {
        // Emit custom event to trigger task list refresh
        window.dispatchEvent(new CustomEvent('taskChanged'));
      }

    } catch (err) {
      console.error('Failed to send message:', err);
      
      // Extract error message from response if available
      const errorObj = err as any;
      let errorMessage = 'Failed to send message. Please try again.';
      
      if (errorObj?.response?.data?.error) {
        errorMessage = errorObj.response.data.error;
      } else if (errorObj?.response?.data?.message) {
        errorMessage = errorObj.response.data.message;
      } else if (errorObj?.message) {
        errorMessage = errorObj.message;
      }
      
      setError(errorMessage);
      
      // Remove temporary message on error
      setMessages((prev) => prev.filter((m) => !m.id.startsWith('temp-')));
    } finally {
      setIsTyping(false);
    }
  }, [currentSession, loadSessions]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Load sessions on mount
  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  return {
    messages,
    sessions,
    currentSession,
    isLoading,
    isTyping,
    error,
    sendMessage,
    loadChatHistory,
    createNewSession,
    selectSession,
    deleteSession,
    updateSessionTitle,
    clearError,
    loadSessions,
  };
};
